import { supabase } from "@/integrations/supabase/client";
import { normalizeLeadTypeId } from "@/lib/leads/leadTypeIds";

import { DEFAULT_MAX_RETRIES, getRetryDelay } from "@/lib/leadStateMachine";

const nowMs = () => Date.now();

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const tierMultiplier = (tier: string | null) => {
  switch ((tier ?? "balanced").toLowerCase()) {
    case "conservative":
      return 0.9;
    case "aggressive":
      return 1.1;
    case "maximum":
      return 1.2;
    case "balanced":
    default:
      return 1.0;
  }
};

const computeContractPressure = (contracts: any[]) => {
  if (!contracts.length) return { score: 0, highest: null };
  let totalCommitted = 0;
  let totalWeightedUrgency = 0;
  let highest = contracts[0];
  let highestScore = 0;

  for (const contract of contracts) {
    const totalDays = Math.max(1, Math.ceil((contract.cycle_end - contract.cycle_start) / (24 * 60 * 60 * 1000)));
    const daysElapsed = Math.max(0, (nowMs() - contract.cycle_start) / (24 * 60 * 60 * 1000));
    const daysRemaining = Math.max(0, Math.ceil((contract.cycle_end - nowMs()) / (24 * 60 * 60 * 1000)));
    const leadsRemaining = Math.max(0, contract.total_leads_committed - contract.leads_delivered);

    const expectedDelivered = contract.total_leads_committed * (daysElapsed / totalDays);
    const deliveryGap = expectedDelivered - contract.leads_delivered;

    const leadsPerDayNeeded = daysRemaining > 0 ? leadsRemaining / daysRemaining : leadsRemaining;
    const paceIntensity = clamp(leadsPerDayNeeded / 1.0, 0, 1);
    const gapRatio = clamp(deliveryGap / Math.max(contract.total_leads_committed, 1), 0, 1);
    const baseUrgency = clamp((paceIntensity * 0.7) + (gapRatio * 0.3), 0, 1);
    const urgencyScore = clamp(baseUrgency * tierMultiplier(contract.priority_tier), 0, 1);

    totalCommitted += contract.total_leads_committed;
    totalWeightedUrgency += urgencyScore * contract.total_leads_committed;

    if (urgencyScore > highestScore) {
      highestScore = urgencyScore;
      highest = contract;
    }
  }

  const score = totalCommitted > 0 ? clamp(totalWeightedUrgency / totalCommitted, 0, 1) : 0;
  return { score, highest };
};

export type RouteNewLeadsResultItem = {
  leadId: string;
  outcome: "ROUTED" | "RETRY_QUEUED" | "STOCKED";
  agentId?: string;
  contractId?: string | null;
};

export type RouteNewLeadsResult = {
  processed: number;
  results: RouteNewLeadsResultItem[];
};

export async function routeNewLeads(options: { limit?: number; dryRun?: boolean } = {}): Promise<RouteNewLeadsResult> {
  const limit = options.limit ?? 10;
  const dryRun = options.dryRun ?? false;

  const { data: leads, error: leadsError } = await supabase
    .from("leads")
    .select("*")
    .eq("status", "NEW")
    .order("created_at", { ascending: true })
    .limit(limit);

  if (leadsError) {
    throw leadsError;
  }

  if (!leads || leads.length === 0) {
    return { processed: 0, results: [] };
  }

  const { data: agents, error: agentsError } = await supabase
    .from("agents")
    .select("*")
    .eq("is_active", true);

  if (agentsError) {
    throw agentsError;
  }

  const { data: contracts, error: contractsError } = await supabase
    .from("contracts")
    .select("*")
    .in("status", ["active", "at_risk"]);

  if (contractsError) {
    throw contractsError;
  }

  const results: RouteNewLeadsResultItem[] = [];

  for (const lead of leads) {
    const { data: claimed, error: claimError } = await supabase
      .from("leads")
      .update({ status: "ROUTING_IN_PROGRESS", last_updated_at: nowMs() })
      .eq("id", lead.id)
      .eq("status", "NEW")
      .select("id")
      .maybeSingle();

    if (claimError || !claimed) {
      continue;
    }

    const canonicalLeadTypeId = normalizeLeadTypeId(lead.lead_type_id);

    const agentEligible = (agents || []).filter((agent: any) => {
      if (agent.is_demo) return false;
      if (agent.vertical !== lead.vertical) return false;
      const prefs = Array.isArray(agent.lead_preferences) ? agent.lead_preferences : [];
      const pref = prefs.find((p: any) => normalizeLeadTypeId(p.leadTypeId) === canonicalLeadTypeId);
      if (!pref || !pref.isActive) return false;
      if (!Array.isArray(agent.licensed_states) || !agent.licensed_states.includes(lead.state)) return false;
      if ((agent.leads_received_today ?? 0) >= (pref.dailyCap ?? 0)) return false;
      if ((agent.leads_received_this_month ?? 0) >= (pref.monthlyCap ?? 0)) return false;
      return true;
    });

    // Contract-level filter: agent must have at least one active contract.
    // If a contract specifies lead_types, the lead must match; empty = unrestricted.
    const eligible = agentEligible.filter((agent: any) => {
      const agentContracts = (contracts || []).filter((c: any) => c.agent_id === agent.id);
      // L1 fix: No active contracts → agent cannot receive leads
      if (agentContracts.length === 0) return false;
      const contractsWithTypes = agentContracts.filter(
        (c: any) => Array.isArray(c.lead_types) && c.lead_types.length > 0
      );
      if (contractsWithTypes.length === 0) return true;
      return contractsWithTypes.some((c: any) => c.lead_types.includes(canonicalLeadTypeId));
    });

    if (eligible.length === 0) {
      const retryCount = (lead.retry_count ?? 0) + 1;
      const canRetry = retryCount < (lead.max_retries ?? DEFAULT_MAX_RETRIES);
      const nextStatus = canRetry ? "RETRY_QUEUED" : "STOCKED";
      const nextRetryAt = canRetry ? nowMs() + getRetryDelay(retryCount - 1) : null;

      if (!dryRun) {
        await supabase
          .from("leads")
          .update({
            status: nextStatus,
            retry_count: retryCount,
            next_retry_at: nextRetryAt,
            last_updated_at: nowMs(),
          })
          .eq("id", lead.id);

        await supabase.from("routing_attempts").insert({
          id: `attempt_${nowMs()}_${Math.random().toString(36).slice(2, 10)}`,
          lead_id: lead.id,
          attempt_number: retryCount,
          source: "LIVE",
          result: nextStatus,
          eligible_agents_count: 0,
          ineligible_agents_count: eligible.length,
          total_agents_evaluated: (agents || []).length,
          guardrails_applied: false,
          pacing_cap_applied: false,
          pool_boost_applied: false,
          retry_priority_applied: false,
          started_at: nowMs(),
          completed_at: nowMs(),
          duration_ms: 0,
        });
      }

      results.push({ leadId: lead.id, outcome: nextStatus });
      continue;
    }

    // L2 fix: prefer contracts that match this lead type for pressure scoring
    const candidates = eligible.map((agent: any) => {
      const agentContracts = (contracts || []).filter((c: any) => c.agent_id === agent.id);
      const typeMatching = agentContracts.filter(
        (c: any) => Array.isArray(c.lead_types) && c.lead_types.length > 0 && c.lead_types.includes(canonicalLeadTypeId)
      );
      const contractsForPressure = typeMatching.length > 0 ? typeMatching : agentContracts;
      const { score: pressureScore, highest } = computeContractPressure(contractsForPressure);
      const performanceScore = clamp((agent.performance_score ?? 0) / 100, 0, 1);
      const score = (performanceScore * 0.6) + (pressureScore * 0.4);
      return { agent, score, highestContract: highest };
    });

    // Attempt to assign lead — try candidates in score order with atomic cap check
    // (mirrors Edge Function route-new-leads logic)
    candidates.sort((a, b) => b.score - a.score);

    let selectedAgent: any = null;
    let contractId: string | null = null;

    for (const candidate of candidates) {
      // Atomic cap check + increment — prevents stale-data and race conditions
      const { data: capResult, error: capError } = await (supabase.rpc as any)(
        "try_increment_agent_cap",
        {
          p_agent_id: candidate.agent.id,
          p_lead_type_id: canonicalLeadTypeId,
        }
      );

      if (capError) {
        console.error(
          `[routeNewLeads] try_increment_agent_cap error for agent ${candidate.agent.id}:`,
          capError.message
        );
        continue;
      }

      const capData = capResult as { success: boolean; error?: string } | null;

      if (!capData?.success) {
        console.log(
          `[routeNewLeads] Agent ${candidate.agent.id} cap exceeded (${capData?.error}), trying next`
        );
        continue;
      }

      // Cap reserved — this agent is confirmed
      selectedAgent = candidate.agent;
      contractId = candidate.highestContract?.id ?? null;
      break;
    }

    if (!selectedAgent) {
      // All candidates exceeded caps
      const retryCount = (lead.retry_count ?? 0) + 1;
      const canRetry = retryCount < (lead.max_retries ?? DEFAULT_MAX_RETRIES);
      const nextStatus = canRetry ? "RETRY_QUEUED" : "STOCKED";
      const nextRetryAt = canRetry ? nowMs() + getRetryDelay(retryCount - 1) : null;

      if (!dryRun) {
        await supabase
          .from("leads")
          .update({
            status: nextStatus,
            retry_count: retryCount,
            next_retry_at: nextRetryAt,
            last_updated_at: nowMs(),
          })
          .eq("id", lead.id);

        await supabase.from("routing_attempts").insert({
          id: `attempt_${nowMs()}_${Math.random().toString(36).slice(2, 10)}`,
          lead_id: lead.id,
          attempt_number: retryCount,
          source: "LIVE",
          result: nextStatus,
          rejection_code: "ALL_CAPS_EXCEEDED",
          rejection_reason: "All eligible agents exceeded daily or monthly caps",
          eligible_agents_count: eligible.length,
          ineligible_agents_count: (agents || []).length - eligible.length,
          total_agents_evaluated: (agents || []).length,
          guardrails_applied: false,
          pacing_cap_applied: true,
          pool_boost_applied: false,
          retry_priority_applied: false,
          started_at: nowMs(),
          completed_at: nowMs(),
          duration_ms: 0,
        });
      }

      results.push({ leadId: lead.id, outcome: nextStatus as "RETRY_QUEUED" | "STOCKED" });
      continue;
    }

    if (!dryRun) {
      await supabase
        .from("leads")
        .update({
          status: "ROUTED",
          routed_to_agent_id: selectedAgent.id,
          routed_to_agent_name: selectedAgent.name,
          owner_agent_id: selectedAgent.id,
          owner_agent_email: selectedAgent.email ?? null,
          contract_id: contractId,
          last_updated_at: nowMs(),
        })
        .eq("id", lead.id);

      await supabase.from("routing_attempts").insert({
        id: `attempt_${nowMs()}_${Math.random().toString(36).slice(2, 10)}`,
        lead_id: lead.id,
        attempt_number: (lead.retry_count ?? 0) + 1,
        source: "LIVE",
        result: "ROUTED",
        agent_id: selectedAgent.id,
        agent_name: selectedAgent.name,
        eligible_agents_count: eligible.length,
        ineligible_agents_count: (agents || []).length - eligible.length,
        total_agents_evaluated: (agents || []).length,
        guardrails_applied: false,
        pacing_cap_applied: false,
        pool_boost_applied: false,
        retry_priority_applied: false,
        started_at: nowMs(),
        completed_at: nowMs(),
        duration_ms: 0,
      });

      // Update contract delivery — atomic RPC increment
      if (contractId) {
        const { error: rpcErr } = await (supabase.rpc as any)("increment_leads_delivered", {
          p_contract_id: contractId,
          p_count: 1,
        });
        if (rpcErr) {
          console.error("[routeNewLeads] increment_leads_delivered failed:", rpcErr.message);
        }
      }
    }

    results.push({
      leadId: lead.id,
      outcome: "ROUTED",
      agentId: selectedAgent.id,
      contractId,
    });
  }

  return { processed: results.length, results };
}
